import { isUserEnrolled } from '@/repos/enrollmentRepo';
import { isUserEnrolledInModule } from '@/repos/moduleEnrollmentRepo';

/**
 * Perfis que ignoram checagem de matrícula para visualização de conteúdo (gestão/preview).
 */
export function isPrivilegedContentRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'manager';
}

export type LessonAccessParams = {
  /** users.id (tabela users), alinhado à coluna enrollments.user_id na área Matrícula */
  systemUserId: string | undefined;
  courseId: string | undefined;
  moduleId?: string | undefined;
  lessonIsFree: boolean | undefined;
  userTenantRole: string | undefined;
};

/**
 * Define se o usuário pode assistir à lição (logado).
 * - Lições is_free: qualquer usuário autenticado.
 * - Admin/manager: sempre.
 * - Matrícula ativa/trial no curso: todo o curso.
 * - Matrícula ativa/trial no módulo: só aquele módulo.
 */
export async function canAccessLessonContent(params: LessonAccessParams): Promise<boolean> {
  const { systemUserId, courseId, moduleId, lessonIsFree, userTenantRole } = params;

  if (isPrivilegedContentRole(userTenantRole)) {
    return true;
  }
  if (lessonIsFree) {
    return true;
  }
  if (!systemUserId || !courseId) {
    return false;
  }

  if (await isUserEnrolled(systemUserId, courseId)) {
    return true;
  }

  if (moduleId && (await isUserEnrolledInModule(systemUserId, moduleId))) {
    return true;
  }

  return false;
}

/** Parâmetros para bloqueio visual na lista/sidebar (síncrono; escopo já resolvido no pai). */
export type LessonNavigationParams = {
  lessonIsFree: boolean | undefined;
  userTenantRole: string | undefined;
  /** Matrícula válida no curso inteiro */
  isEnrolledInCourse: boolean;
  /** Matrícula válida neste módulo (quando escopo parcial) */
  isEnrolledInModule?: boolean;
};

/**
 * Se o aluno pode tratar a lição como “aberta” na navegação (cards/sidebar).
 */
export function isLessonUnlockedForNavigation(params: LessonNavigationParams): boolean {
  if (isPrivilegedContentRole(params.userTenantRole)) {
    return true;
  }
  if (params.lessonIsFree) {
    return true;
  }
  if (params.isEnrolledInCourse) {
    return true;
  }
  if (params.isEnrolledInModule) {
    return true;
  }
  return false;
}

/** Módulo visível na lista mas bloqueado para assistir */
export function isModuleAccessible(params: {
  userTenantRole: string | undefined;
  isEnrolledInCourse: boolean;
  isEnrolledInModule: boolean;
}): boolean {
  if (isPrivilegedContentRole(params.userTenantRole)) {
    return true;
  }
  if (params.isEnrolledInCourse) {
    return true;
  }
  return params.isEnrolledInModule;
}
